import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Heart } from 'lucide-react'
import { KindIcon } from './KindIcon'
import { listChipCount, listSummary } from '../lib/listSummary'
import { chipOrder } from '../lib/chipOrder'
import { chipFold } from '../lib/chipFold'
import type { usePointerReorder } from '../lib/usePointerReorder'
import type { List, ListItem, ListRun } from '../lib/types'

/** The one group every chip is in: any chip can be dropped anywhere in the
 *  row (`lib/chipOrder.ts` says why there is no section any more). */
export const CHIP_GROUP = 'chips'

/** The id the Shortlist chip carries: `selectedId` and `onOpen` speak it for
 *  the one view that is not a row of `lists`. A real list id is a Postgres
 *  UUID and can never be this literal. */
export const SHORTLIST_CHIP = 'shortlist'

/**
 * The utility lists as one row of chips (Lists, direction B): Tasks 1 ·
 * Groceries 2 · Purchases 15 · Kindred prep · DJs. Each was a 64px row with a
 * tile; the row is what made five small lists a screen of their own and
 * pushed the Shortlist, which is what the page is for, below the fold.
 *
 * The row is a segmented view switch, not a set of links: Shortlist first,
 * then the lists, exactly one of them selected (`selectedId`), and a tap
 * swaps the body under the row in place (`Lists.tsx` draws it). Archived is
 * the exception — a toggle for the rows under the row, not a view, so it
 * never takes the selection.
 *
 * The chips wrap (Doug, 6 Oct: "make the pills two rows") rather than
 * scroll sideways. Past the first `CHIP_CAP` lists they fold behind a
 * "+N more" chip (Doug, 17 Oct: three rows was too many, and the third held
 * the lists he rarely opens) — `lib/chipFold.ts` says which, in his own
 * order; a tap on "+N more" shows them all until the tab is left.
 *
 * A tap opens the list. A long press opens its menu, and a long press then
 * drag reorders it anywhere in the rows — `usePointerReorder` bound with
 * `axis: 'wrap'`, which carries the chip under the finger in both directions
 * and draws the drop line on the side of the target the finger is on. A
 * mouse picks a chip up the same way, by holding; the phone is the surface
 * for this screen and the desk has its own.
 *
 * `archived` is a chip too, at the end, so the row's one control for the rest
 * of the lists is where the rest of the lists are.
 */
export function ListChips({
  lists, itemsOf, runsOf, drag, archivedCount, archivedOpen, onToggleArchived, onOpen,
  selectedId, shortlistCount, visible = true,
}: {
  /** Every list, archived or not; the archived ones are counted, not drawn. */
  lists: readonly List[]
  itemsOf: (listId: string) => ListItem[]
  runsOf: (listId: string) => ListRun[]
  drag: ReturnType<typeof usePointerReorder>
  archivedCount: number
  archivedOpen: boolean
  onToggleArchived: () => void
  /** Selects a view: a list's id, or `SHORTLIST_CHIP`. */
  onOpen: (id: string) => void
  /** The selected view — a list's id or `SHORTLIST_CHIP`. An archived list
   *  can be selected and has no chip here, so no chip is on then. */
  selectedId: string
  /** Saved events; the chip omits it at 0. */
  shortlistCount: number
  /** Whether the tab is on screen. A hidden pane has no layout, so the
   *  scroll-into-view below has to run again when it is shown. */
  visible?: boolean
}) {
  const rowRef = useRef<HTMLDivElement>(null)
  // Bring the selected chip into view. The rows wrap now, so no chip is off
  // to the side any more and this moves nothing; it stays for a width narrow
  // enough that the chips run past the fold. No smooth behaviour — a cold
  // load should land, not travel.
  useEffect(() => {
    if (!visible) return
    rowRef.current?.querySelector('.listchip.is-on')
      ?.scrollIntoView({ inline: 'nearest', block: 'nearest' })
  }, [selectedId, visible])

  // Session state only: the fold closes again on the next visit, which is
  // the point of it. The selected list is always among `shown`.
  const [expanded, setExpanded] = useState(false)
  const ordered = chipOrder(lists)
  const fold = chipFold(ordered, selectedId, expanded)
  const shown = fold.shown
  // Whether there is a fold to close again: the same rule, unexpanded.
  const foldable = expanded && chipFold(ordered, selectedId, false).hidden.length > 0
  const now = new Date()

  return (
    <div className="listchips" ref={rowRef} role="tablist" aria-label="Lists">
      <button
        type="button"
        role="tab"
        aria-selected={selectedId === SHORTLIST_CHIP}
        className={`listchip${selectedId === SHORTLIST_CHIP ? ' is-on' : ''}`}
        onClick={() => onOpen(SHORTLIST_CHIP)}
      >
        <span className="listchip__face">
          <span className="listchip__glyph"><Heart size={16} aria-hidden="true" /></span>
          <span className="listchip__name">Shortlist</span>
          {shortlistCount > 0 && <span className="listchip__count">{shortlistCount}</span>}
        </span>
      </button>
      {shown.map(l => {
        const items = itemsOf(l.id)
        const ring = l.kind === 'template' ? listSummary(l, items, runsOf(l.id), now).ring : undefined
        const count = listChipCount(l, items)
        return (
          <button
            key={l.id}
            type="button"
            role="tab"
            aria-selected={selectedId === l.id}
            className={`listchip${selectedId === l.id ? ' is-on' : ''} ${drag.classFor(l.id)}`}
            onClick={() => onOpen(l.id)}
            {...drag.bind(l.id, CHIP_GROUP, 'wrap')}
          >
            <span className="listchip__face">
              {ring
                ? <ChipRing done={ring.done} total={ring.total} />
                : <span className="listchip__glyph"><KindIcon kind={l.kind} size={16} /></span>}
              <span className="listchip__name">{l.name}</span>
              {count != null && <span className="listchip__count">{count}</span>}
            </span>
          </button>
        )
      })}
      {fold.hidden.length > 0 && (
        <button
          type="button"
          className="listchip listchip--more"
          aria-expanded={false}
          aria-label={`${fold.hidden.length} more lists: ${fold.hidden.map(l => l.name).join(', ')}`}
          onClick={() => setExpanded(true)}
        >
          <span className="listchip__face">
            <span className="listchip__name">+{fold.hidden.length} more</span>
          </span>
        </button>
      )}
      {foldable && (
        <button
          type="button"
          className="listchip listchip--more"
          aria-expanded={true}
          onClick={() => setExpanded(false)}
        >
          <span className="listchip__face">
            <span className="listchip__name">Fewer</span>
          </span>
        </button>
      )}
      {archivedCount > 0 && (
        <button
          type="button"
          className={`listchip listchip--archived${archivedOpen ? ' is-open' : ''}`}
          aria-expanded={archivedOpen}
          onClick={onToggleArchived}
        >
          <span className="listchip__face">
            <span className="listchip__name">Archived · {archivedCount}</span>
          </span>
        </button>
      )}
    </div>
  )
}

/** A checklist's 16px ring at the run's percentage — the chip-sized
 *  `Ring` of `Lists.tsx`, which is a 44px slot and would not shrink. */
function ChipRing({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  return (
    <span
      className="listchip__ring"
      role="img"
      aria-label={`${done} of ${total} done`}
      style={{ '--pct': `${pct}%` } as CSSProperties}
    />
  )
}
